import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAccount?: boolean;
  redirectTo?: string;
  accountCheckPath?: string;
}

/**
 * Protected route wrapper that ensures user is authenticated
 * and optionally checks for account existence
 * Matches original ProtectedRoute behavior
 */
export function ProtectedRoute({
  children,
  requireAccount = false,
  redirectTo = '/login',
  accountCheckPath = '/create-account',
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [checkingFlow, setCheckingFlow] = useState(true);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    const checkUserFlow = async () => {
      // Wait for auth to finish loading
      if (isLoading) {
        return;
      }

      // If no user after loading, we're done checking
      if (!user) {
        setCheckingFlow(false);
        return;
      }

      // Check if user has an account (if required)
      if (requireAccount) {
        // If user has no accountId and not already on create-account page, redirect
        if (!user.accountId && location.pathname !== accountCheckPath) {
          setRedirectPath(accountCheckPath);
          setCheckingFlow(false);
          return;
        }
      }

      setCheckingFlow(false);
    };

    checkUserFlow();
  }, [user, isLoading, requireAccount, location.pathname, accountCheckPath]);

  // Show loading state while checking auth or flow
  if (isLoading || checkingFlow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Redirect if needed (e.g., to create-account)
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Render children if authenticated (and has account if required)
  return <>{children}</>;
}
